import React, { useEffect } from 'react';
import {
  Caption,
  IconButton,
  RegularButton,
  Input,
  Typography,
  useMediaQuery,
  Divider,
} from 'fronton-react';
import { Controller, UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import { styled } from 'linaria/react';
import { css } from 'linaria';
import get from 'lodash/get';
import { PlusIcon, TrashIcon } from '@fronton/icons-react';

import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { useTranslation } from 'react-i18next';
import { InputContainerRowDoorsWindows, InputContainerColumnDoorsWindows } from '../styles';
import { goodsDefaultMeasurments } from '../const';
import { getGoodsLabels } from './helpers/get-goods-labels';

export const GoodsDimensionsForm = ({
  name,
  formProps,
  type,
  title,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  name: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formProps: UseFieldArrayReturn<FieldValues, any>;
  type: string;
  title: string;
}) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const isMobile = useMediaQuery({
    maxWidth: 767,
  });

  // Для того чтобы обновлялись поля количества окон и дверей, после удаления или добавления их из массива полей
  useEffect(() => {
    const fieldsCount = formProps.fields.length;
    setValue(type, `${fieldsCount}`);
  }, [setValue, formProps.fields.length, type]);

  const countOfDimentionFields = formProps.fields.length;
  const goodsLabels = getGoodsLabels(t);

  const onAddDimentionsItemClick = () => {
    gaClickOnGoods({ title, name: `Добавить ${goodsLabels[name].small}` });
    formProps.append({
      width: goodsDefaultMeasurments[name].width,
      height: goodsDefaultMeasurments[name].height,
    });
  };

  return (
    <DoorsContainer>
      <HeadingContainer
        isOpened={!!countOfDimentionFields}
        onClick={!!countOfDimentionFields ? undefined : onAddDimentionsItemClick}>
        <Typography variant="h3" color={countOfDimentionFields > 0 ? 'text-primary' : 'text-minor'}>
          {goodsLabels[name].heading}
        </Typography>

        {countOfDimentionFields === 0 ? (
          <IconButton
            data-testid={`add-button-${goodsLabels[name].small}`}
            as="button"
            size="l"
            variant="secondary"
            label={`Добавить ${goodsLabels[name].small}`}>
            <PlusIcon />
          </IconButton>
        ) : null}
      </HeadingContainer>
      {countOfDimentionFields === 0 || isMobile ? <Divider /> : null}
      {formProps.fields.map((field, idx) => (
        <DimensionsInputsContainer key={field.id}>
          <InputContainerRowDoorsWindows direction="row">
            <InputContainerColumnDoorsWindows last>
              <Controller
                name={`${name}.[${idx}].width`}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('calculator.dimensions.doorsWindows.goodsErrors.width.required'),
                  },
                  pattern: {
                    value: /^[0-9]{1,2}([,.][0-9]{1,2})?$/,
                    message: t('calculator.dimensions.doorsWindows.goodsErrors.width.pattern'),
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    data-testid="input-width"
                    fullWidth
                    value={value}
                    allowClear
                    onChange={onChange}
                    label={t('calculator.dimensions.doorsWindows.dimensions.width')}
                    aria-label={t('calculator.dimensions.doorsWindows.dimensions.width')}
                    placeholder={t('calculator.dimensions.doorsWindows.dimensions.width')}
                    error={Boolean(get(errors, `${name}.${idx}.width`))}
                    caption={
                      <Caption
                        error={Boolean(get(errors, `${name}.${idx}.width`))}
                        message={get(errors, `${name}.${idx}.width.message`)}
                      />
                    }
                  />
                )}
              />
            </InputContainerColumnDoorsWindows>
            <InputContainerColumnDoorsWindows last>
              <Controller
                name={`${name}.[${idx}].height`}
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t('calculator.dimensions.doorsWindows.goodsErrors.height.required'),
                  },
                  pattern: {
                    value: /^[0-9]{1,2}([,.][0-9]{1,2})?$/,
                    message: t('calculator.dimensions.doorsWindows.goodsErrors.height.pattern'),
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    data-testid="input-height"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    allowClear
                    label={t('calculator.dimensions.doorsWindows.dimensions.height')}
                    aria-label={t('calculator.dimensions.doorsWindows.dimensions.height')}
                    placeholder={t('calculator.dimensions.doorsWindows.dimensions.height')}
                    error={Boolean(get(errors, `${name}.${idx}.height`))}
                    caption={
                      <Caption
                        error={Boolean(get(errors, `${name}.${idx}.height`))}
                        message={get(errors, `${name}.${idx}.height.message`)}
                      />
                    }
                  />
                )}
              />
            </InputContainerColumnDoorsWindows>
          </InputContainerRowDoorsWindows>
          <IconButton
            data-testid={`delete-${name}-array-button`}
            label={`Удалить ${goodsLabels[name].small}`}
            size="l"
            onClick={() => {
              gaClickOnGoods({ title, name: `Удалить ${goodsLabels[name].small}` });
              formProps.remove(idx);
            }}
            className={
              get(errors, `${name}`) && Boolean(get(errors, `${name}.${idx}`)) ? buttonClassname : ''
            }>
            <TrashIcon />
          </IconButton>
        </DimensionsInputsContainer>
      ))}
      {countOfDimentionFields > 0 ? (
        <DimensionsAddButtonContainer>
          <RegularButton
            iconLeft={<PlusIcon />}
            size="l"
            variant="pseudo"
            rounded
            onClick={onAddDimentionsItemClick}>
            {t('calculator.dimensions.doorsWindows.addMore')} {goodsLabels[name].small}
          </RegularButton>
        </DimensionsAddButtonContainer>
      ) : null}
    </DoorsContainer>
  );
};

const DoorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: var(--space-50);
  margin-bottom: var(--space-300);

  &:last-of-type {
    margin-bottom: 0px;
  }

  @media (--only-mobile) {
    margin-top: 0px;
    margin-bottom: var(--space-200);

    &:last-of-type {
      margin-bottom: 0px;
    }
  }
`;

const DimensionsInputsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-200);
  margin-bottom: var(--space-200);

  @media (--only-mobile) {
    margin-top: var(--space-300);
    margin-bottom: 0px;
  }
`;

const DimensionsAddButtonContainer = styled.div`
  position: relative;
  display: flex;
  @media (--only-mobile) {
    margin-top: var(--space-200);
  }
`;

const buttonClassname = css`
  position: relative;
  top: -11.75px;
`;

const HeadingContainer = styled.div<{ isOpened: boolean; onClick?: () => void }>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${props => (props.isOpened ? 'var(--space-350)' : 'var(--space-50)')};
  cursor: ${props => (props.isOpened ? 'auto' : 'pointer')};

  &:hover {
    span {
      color: var(--text-primary) !important;
      transition: 0.3s;
    }
    button[data-variant='secondary'] {
      --button-color: var(--text-primary);
    }
  }

  @media (--only-mobile) {
    margin-bottom: var(--space-50);
  }
`;
